//Dependencies............................................
import React, { ReactNode, useState } from "react";
import axios from 'axios';
import {
    Modal,
    Form,
    Upload,
    UploadFile,
    UploadProps,
    Card,
    Space,
    Typography,
    Divider,
    Empty,
    Tooltip,
    Alert,
    Button,
} from 'antd';
import { UploadChangeParam } from "antd/es/upload";
import { FilePdfOutlined, DownloadOutlined, InboxOutlined, LockOutlined, DeleteOutlined } from '@ant-design/icons';
import {
    useQueryClient,
    useMutation,
} from '@tanstack/react-query'

//Components..............................................
import {BASE_URL} from '../../../utils/constants';
import { VehicleItem } from "../../../utils/interfaces";
import { NotificationInstance } from "antd/es/notification/interface";
import { useHasAction } from "../../../hooks/useHasAction";
import DeleteModal from "../../GlobalComponents/Modals/DeleteModal";

const { Text } = Typography;
const { Dragger } = Upload;

interface Props {
    vehicleId: number,
    showModal: boolean,
    onClose: () => void
    api: NotificationInstance
}

interface WiringFormData {
    pdf_file: {
        file: File,
        fileList: FileList
    }
}

const downloadWiringPdf = (fileName: string) => {
    axios({
        url: `${BASE_URL}/get_pdf/${fileName}`, //your url
        method: 'GET',
        responseType: 'blob', // important
    }).then((response) => {
        // create file link in browser's memory
        const href = URL.createObjectURL(response.data);

        // create "a" HTML element with href to file & click
        const link = document.createElement('a');
        link.href = href;

        link.setAttribute('download', fileName); //or any other extension
        document.body.appendChild(link);
        link.click();

        // clean up "a" element & remove ObjectURL
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
    })
}

const EditWiringPdfModal: React.FC<Props> = ({ vehicleId, showModal, onClose, api }: Props) => {

    const canDownloadInstructionsVehicleConfig: boolean = useHasAction("vehicle_config_download_instructions");
    const canUploadInstructionsVehicleConfig: boolean = useHasAction("vehicle_config_upload_instructions");

    const [form] = Form.useForm<WiringFormData>();

    const [fileList, setFileList] = useState<UploadFile[]>([])
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

    const queryClient = useQueryClient()

    const getVehicleData = () : VehicleItem | undefined => {
        // 'vehicleList' is now an infinite query (see VehicleListView.tsx) -
        // its cache entry is {pages: [{items, total}, ...]}, not a flat
        // array, so the vehicle has to be searched for across pages.
        const data = queryClient.getQueryData<{ pages: { items: VehicleItem[] }[] }>(['vehicleList'])
        if (data) {
            for (const page of data.pages) {
                const vehicle = page.items.find((item) => item.id === vehicleId)
                if (vehicle) {
                    return vehicle
                }
            }
        }
        return undefined;
    }

    const vehicleData = getVehicleData()
    const vehicleLabel = vehicleData
        ? [vehicleData.manufacturer, vehicleData.model, vehicleData.type].filter(Boolean).join(' ')
        : undefined;

    // 'vehicleList' is a paginated useInfiniteQuery - invalidating it would
    // refetch every page already loaded (potentially thousands of vehicles)
    // just to reflect one field changing on one vehicle. Patch that single
    // vehicle's wiring_instruction directly in the cache instead.
    type VehicleListCache = { pages: { items: VehicleItem[]; total: number }[] };

    const patchWiringInstructionInCache = (wiringInstruction: string | null) => {
        queryClient.setQueryData<VehicleListCache>(
            ['vehicleList'],
            (old?: VehicleListCache) => {
                if (!old) return old;
                return {
                    ...old,
                    pages: old.pages.map((page) => ({
                        ...page,
                        items: page.items.map((item) =>
                            item.id === vehicleId ? { ...item, wiring_instruction: wiringInstruction } : item
                        ),
                    })),
                };
            }
        );
    };

    const uploadWiringPdfMutation = useMutation({
        mutationFn: (values: WiringFormData) => {
            const formData = new FormData();
            formData.append("vehicle_id", vehicleId.toString())
            formData.append('pdf_file', values.pdf_file.file)
            return axios.post(`${BASE_URL}/upload_pdf_car`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
        },
        onSuccess: (response) => patchWiringInstructionInCache(response.data.content ?? null),
        onError: (error) => console.error('Error adding new wiring', error)
    })

    const removeWiringPdfMutation = useMutation({
        mutationFn: () => axios.post(`${BASE_URL}/remove_pdf_car/${vehicleId}`),
        onSuccess: () => patchWiringInstructionInCache(null),
    })

    const handleRemoveWiringPdf = () => {
        removeWiringPdfMutation.mutate(undefined, {
            onSuccess: () => {
                setShowRemoveConfirm(false);
                api.success({
                    title: 'Wiring PDF removed successfully',
                    placement: 'bottomRight',
                    showProgress: true
                });
            },
            onError: (error) => {
                api.error({
                    title: 'Error removing wiring PDF',
                    description: error.message,
                    placement: 'bottomRight',
                    showProgress: true
                });
            }
        });
    };

    const onFinish = (values: WiringFormData) => {
        // console.log('Received values of form: ', values);
        uploadWiringPdfMutation.mutate(values, {
            onSuccess: () =>  {
                setFileList([]);
                form.setFieldValue('pdf_file', undefined)
                onClose()
                api.success({
                    title: 'Wiring PDF uploaded successfully',
                    placement: 'bottomRight',
                    showProgress: true
                });
            },
            onError: (error) => {
                api.error({
                    title: 'Error uploading wiring PDF',
                    description: error.message,
                    placement: 'bottomRight',
                    showProgress: true
                });
            }
        });
    };

    const handleClose = () => {
        setFileList([]);
        form.resetFields();
        onClose();
    }

    const getWiringFileNode = (): ReactNode => {
        if (vehicleData && vehicleData.wiring_instruction) {
            const fileName = vehicleData.wiring_instruction;
            const card = (
                <Card
                    hoverable={canDownloadInstructionsVehicleConfig}
                    size="small"
                    onClick={() => {
                        if (canDownloadInstructionsVehicleConfig) {
                            downloadWiringPdf(fileName)
                        }
                    }}
                    style={{
                        cursor: canDownloadInstructionsVehicleConfig ? 'pointer' : 'not-allowed',
                        opacity: canDownloadInstructionsVehicleConfig ? 1 : 0.55,
                        flex: 1,
                    }}
                >
                    <Space align="center" style={{ width: '100%' }}>
                        <FilePdfOutlined style={{ fontSize: 28, color: '#f5222d' }} />
                        <div>
                            <Text strong style={{ display: 'block' }}>{fileName}</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {canDownloadInstructionsVehicleConfig
                                    ? 'Click to download'
                                    : 'You do not have permission to download this file'}
                            </Text>
                        </div>
                        {canDownloadInstructionsVehicleConfig
                            ? <DownloadOutlined style={{ marginLeft: 'auto', color: '#8c8c8c' }} />
                            : <LockOutlined style={{ marginLeft: 'auto', color: '#8c8c8c' }} />}
                    </Space>
                </Card>
            )
            return (
                <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                    {canDownloadInstructionsVehicleConfig
                        ? card
                        : <Tooltip title="You do not have permission to download this file">{card}</Tooltip>}
                    <Tooltip title={canUploadInstructionsVehicleConfig ? "Remove wiring instructions" : "You do not have permission to remove this file"}>
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            disabled={!canUploadInstructionsVehicleConfig || uploadWiringPdfMutation.isPending}
                            onClick={() => setShowRemoveConfirm(true)}
                        />
                    </Tooltip>
                </div>
            )
        } else {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No wiring instructions uploaded yet"
                    style={{ margin: '8px 0' }}
                />
            )
        }
    }

    const uploadProps: UploadProps = {
        accept: ".pdf",
        maxCount: 1,
        multiple: false,
        disabled: !canUploadInstructionsVehicleConfig,
        onRemove: () => {
            setFileList([]);
            form.setFieldValue('pdf_file', undefined)
        },
        beforeUpload: file => {
            setFileList([file]);
            // form.setFieldValue('pdf_file', file)
            return false;
        },
        fileList: fileList,
    };

    return(
    <>
    <Modal
        title={
            <Space>
                <FilePdfOutlined style={{ color: '#f5222d' }} />
                <span>Wiring Instructions{vehicleLabel ? ` — ${vehicleLabel}` : ''}</span>
            </Space>
        }
        open={showModal}
        okText="Upload"
        cancelText="Close"
        okButtonProps={{
            autoFocus: false,
            htmlType: 'submit',
            loading: uploadWiringPdfMutation.isPending,
            disabled: !canUploadInstructionsVehicleConfig || fileList.length === 0 || uploadWiringPdfMutation.isPending,
        }}
        cancelButtonProps={{ disabled: uploadWiringPdfMutation.isPending }}
        closable={!uploadWiringPdfMutation.isPending}
        mask={{ closable: !uploadWiringPdfMutation.isPending }}
        onCancel={handleClose}
        modalRender={(dom) => (
          <Form
            layout="vertical"
            form={form}
            name="form_upload_pdf"
            clearOnDestroy
            disabled={!canUploadInstructionsVehicleConfig}
            onFinish={(values) => onFinish(values)}
          >
            {dom}
          </Form>
        )}>
        <Text type="secondary">Current file</Text>
        <div style={{ marginTop: 8, marginBottom: 20 }}>
            {getWiringFileNode()}
        </div>

        <Divider style={{ margin: '12px 0' }} />

        <Text type="secondary">Upload new file</Text>
        <div style={{ marginTop: 8 }}>
            {!canUploadInstructionsVehicleConfig && (
                <Alert
                    type="warning"
                    showIcon
                    title="You do not have permission to upload wiring instructions"
                    style={{ marginBottom: 12 }}
                />
            )}
            <Form.Item
                name="pdf_file"
                style={{ marginBottom: 0 }}
                rules={[{
                    required: true,
                    validator: (_, value: UploadChangeParam) =>{
                        if(value && value.fileList && value.fileList.length) {
                            return Promise.resolve()
                        } else {
                            return Promise.reject(new Error('Please add one file'))
                        }
                    }
                }]}>
                <Dragger {...uploadProps}>
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">Click or drag a PDF file to this area to upload</p>
                    <p className="ant-upload-hint">Only .pdf files are supported, one at a time</p>
                </Dragger>
            </Form.Item>
        </div>
    </Modal>
    <DeleteModal
        showModal={showRemoveConfirm}
        onClose={() => setShowRemoveConfirm(false)}
        onDelete={handleRemoveWiringPdf}
        title="Remove Wiring Instructions"
        content={<Text>Are you sure you want to remove the wiring instructions for this vehicle? This action is permanent.</Text>}
        loadingDelete={removeWiringPdfMutation.isPending}
        disabledCancel={removeWiringPdfMutation.isPending}
        disableDelete={removeWiringPdfMutation.isPending}
    />
    </>)
}
export default EditWiringPdfModal;
